import type { ViewComponent } from './viewTypes'

// AB#1937/1955 — re-packs a render list onto contiguous grid rows. The stored `row` numbers are EXPLICIT
// and leave gaps once a collapsed group's fields are dropped; re-flowing keeps the grid contiguous.
// Components that share the same STORED row stay on the same render row (preserving the 2-up / multi-up
// layout). AB#1955 — placement is OCCUPANCY-aware: each stored-row group lands on the earliest render row
// (>= a running `floor` that advances one row per group) where EVERY one of its components' columns are
// free for its `rowSpan`. This lets a short field tuck BESIDE a tall (rowSpan) component in a free column
// (the AB#1952 map-card: a map at col1 rowSpan3 with address fields stacked at col13) instead of being
// pushed below it. For a layout with no such column gap it is identical to the old tallest-rowSpan
// row-advance — a group whose columns collide with a still-spanning component is pushed past it, exactly as
// before. `colSpan`/`rowSpan` are untouched.
//
// AB#2559 — it also closes a LEADING column gap, so the caller gets a `col` back rather than reading
// `component.col`. Suppression is contextual and happens upstream: RelatedEntityListSlot strips the parent-FK
// component from a child modal (the modal always knows its parent), showIf drops fields per record, hidden
// fields never render. Stored columns are absolute, so losing the left-hand field of a 2-up row used to
// leave a permanent hole with its partner marooned in the right column — the Customer Account > Partner
// Functions modal showed an empty cell beside "Partner Function". Same reasoning as the AB#2476 emptied-band
// suppression: fix it where the context is known, at render time, so every cause is covered at once.
//
// Only a LEADING gap closes, and only when the shifted columns are actually free on the render row the group
// already landed on. A whole group shifts together by one delta, so relative spacing inside it survives.
// Both conditions matter: an INTERIOR gap is more plausibly deliberate and is left alone, and the occupancy
// test is what preserves the AB#1952 map card — those address fields sit at col 13 precisely because a tall
// map holds cols 1-12, so the shift does not fit and they stay put.
export function packRows(components: ViewComponent[]): Array<{ component: ViewComponent; row: number; col: number }> {
    const out: Array<{ component: ViewComponent; row: number; col: number }> = []
    // occ[renderRow] = the set of columns (1..24) already taken at that render row, accounting for rowSpans.
    const occ = new Map<number, Set<number>>()
    const fits = (row: number, span: number, col: number, colSpan: number): boolean => {
        for (let r = row; r < row + span; r++) {
            const set = occ.get(r)
            if (!set) { continue }
            for (let c = col; c < col + colSpan; c++) { if (set.has(c)) { return false } }
        }
        return true
    }
    const occupy = (row: number, span: number, col: number, colSpan: number): void => {
        for (let r = row; r < row + span; r++) {
            let set = occ.get(r)
            if (!set) { set = new Set<number>(); occ.set(r, set) }
            for (let c = col; c < col + colSpan; c++) { set.add(c) }
        }
    }
    let floor = 1
    let i = 0
    while (i < components.length) {
        const storedRow = components[i]!.row
        // Gather the contiguous run of components sharing this stored row — they render on one row together.
        const bucket: ViewComponent[] = []
        while (i < components.length && components[i]!.row === storedRow) { bucket.push(components[i]!); i++ }
        // Earliest render row >= floor where EVERY bucket member's columns are free for its rowSpan.
        let r = floor
        while (!bucket.every(c => fits(r, c.rowSpan || 1, c.col, c.colSpan || 1))) { r++ }
        // AB#2559 — close a leading gap, but only into space that is genuinely free on THIS row: the row is
        // already settled, so shifting must not change which row anything lands on.
        const delta = Math.min(...bucket.map(c => c.col)) - 1
        const shift = delta > 0 && bucket.every(c => fits(r, c.rowSpan || 1, c.col - delta, c.colSpan || 1))
            ? delta
            : 0
        for (const c of bucket) {
            const col = c.col - shift
            out.push({ component: c, row: r, col })
            occupy(r, c.rowSpan || 1, col, c.colSpan || 1)
        }
        floor = r + 1
    }
    return out
}
